import Dexie, { type Table } from 'dexie'

/**
 * Entry stored in IndexedDB
 */
interface StorageEntry {
  key: string
  value: unknown
  updatedAt: number
}

/**
 * Dexie database for Nestify storage
 */
class CaPeLDB extends Dexie {
  storage!: Table<StorageEntry, string>

  constructor() {
    super('CaPeLDB')
    this.version(1).stores({
      storage: 'key' // Primary key is 'key'
    })
  }
}

/**
 * Storage adapter using IndexedDB via Dexie.js
 * Provides async key-value storage with no size limits (unlike localStorage)
 */
export class DexieStorageAdapter {
  private db: CaPeLDB

  constructor() {
    this.db = new CaPeLDB()
  }

  async get<T>(key: string): Promise<T | null> {
    try {
      const entry = await this.db.storage.get(key)
      const value = entry ? (entry.value as T) : null
      if (import.meta.env.DEV) {
        const size = entry ? JSON.stringify(entry.value).length : 0
        console.log(
          `[Dexie] GET ${key} → ${value ? `${(size / 1024).toFixed(1)} KB` : 'null'}`
        )
      }
      return value
    } catch (error) {
      console.error(`[Dexie] GET ${key} → ERROR`, error)
      return null
    }
  }

  async set<T>(key: string, value: T): Promise<void> {
    try {
      // Serialize to plain object to avoid DataCloneError with Vue proxies
      // IndexedDB's structured clone algorithm cannot handle Proxy objects
      const serialized = JSON.stringify(value)
      const plainValue = JSON.parse(serialized)
      await this.db.storage.put({
        key,
        value: plainValue,
        updatedAt: Date.now()
      })
      if (import.meta.env.DEV) {
        console.log(
          `[Dexie] SET ${key} → ${(serialized.length / 1024).toFixed(1)} KB`
        )
      }
    } catch (error) {
      console.error(`[Dexie] SET ${key} → ERROR`, error)
      throw error
    }
  }

  async remove(key: string): Promise<void> {
    try {
      await this.db.storage.delete(key)
      if (import.meta.env.DEV) {
        console.log(`[Dexie] REMOVE ${key}`)
      }
    } catch (error) {
      console.error(`[Dexie] REMOVE ${key} → ERROR`, error)
      // Don't throw - removing non-existent key should be silent
    }
  }

  async clear(): Promise<void> {
    try {
      await this.db.storage.clear()
    } catch (error) {
      console.error('[DexieStorageAdapter] Failed to clear storage', error)
      throw error
    }
  }

  async keys(): Promise<string[]> {
    try {
      const entries = await this.db.storage.toArray()
      return entries.map((e) => e.key)
    } catch (error) {
      console.error('[DexieStorageAdapter] Failed to get keys', error)
      return []
    }
  }

  async has(key: string): Promise<boolean> {
    try {
      const count = await this.db.storage.where('key').equals(key).count()
      const exists = count > 0
      if (import.meta.env.DEV) {
        console.log(`[Dexie] HAS ${key} → ${exists}`)
      }
      return exists
    } catch (error) {
      console.error(`[Dexie] HAS ${key} → ERROR`, error)
      return false
    }
  }

  /**
   * Check if IndexedDB is available and working
   */
  async isAvailable(): Promise<boolean> {
    try {
      await this.db.storage.put({
        key: '_test',
        value: { test: true },
        updatedAt: Date.now()
      })
      await this.db.storage.delete('_test')
      return true
    } catch {
      return false
    }
  }
}

export const dexieStorageInstance = new DexieStorageAdapter()
